import {FontAwesomeIcon as Icon} from "@fortawesome/react-fontawesome"
import Countdown from "components/Countdown"
import Modal from 'components/Modal'
import {Auction, BidAgent, Subject, Tireset, Vehicle} from "domain-models"
import {currency, make, model, translate} from 'filters'
import _ from 'lodash'
import Form from 'mixins/form'
import {FormType} from "mixins/FormTypes"
import {Http} from "mixins/http"
import React from "react"
import {fetchIndicators, setAuctions} from "redux/actions"
import {store} from 'store'
import {connect} from "react-redux"
import {GlobalState} from "../../state"
import Trans from '../../components/Trans'

type BidAgentModalProps = {
  auction: Auction
  hideModal: () => void
  auctions: Array<Auction>
}

type BidAgentModalState = {
  max_bid: number
  form: FormType
  deactivateBusy: boolean
  subject: Tireset | Vehicle
}

type BidAgentResponse = {
  success: boolean
  agent: BidAgent
  isStillOutbid?: boolean
}

class BidAgentModal extends React.Component<BidAgentModalProps, BidAgentModalState> {
  private form: FormType = new Form({max_bid: null})

  constructor(props: BidAgentModalProps) {
    super(props)
    const agent = _.first(this.props.auction.round.agents)
    const subject = _.first(props.auction.subjects)
    let subjectData

    if (subject!.SubjectType === 'Vehicle') {
      subjectData = subject!.vehicle as Vehicle
    } else {
      subjectData = subject!.tireset as Tireset
    }


    this.state = {
      form: this.form,
      max_bid: agent ? agent.max_bid : 0,
      deactivateBusy: false,
      subject: subjectData
    }
  }

  changeMaxBid = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({max_bid: _.toInteger(event.target.value)})
  }

  saveBidAgent = async () => {
    this.form.max_bid = this.state.max_bid
    this.form.auction = this.props.auction
    this.setState({
      form: this.form
    })

    const res = await Http.post<BidAgentResponse>('/bid-agent', {state: this, form: this.state.form})
    if (res && res.success) {
      // Create new objects to trigger React re-render (don't mutate)
      const updatedRound = {...this.props.auction.round, agents: [res.agent]}
      const updatedAuction = {
        ...this.props.auction,
        round: updatedRound,
        // Use backend calculation - user might still be outbid if agent's max bid isn't high enough
        isOutbid: res.isStillOutbid || false
      }
      
      const updatedAuctions = this.props.auctions.map(a => 
        a.Id === updatedAuction.Id ? updatedAuction : a
      )
      store.dispatch(setAuctions(updatedAuctions))
      await fetchIndicators()
      this.props.hideModal()
    }
  }

  updateBidAgent = async () => {
    this.form.max_bid = this.state.max_bid
    this.form.auction = this.props.auction
    this.setState({
      form: this.form
    })

    const res = await Http.put<BidAgentResponse>('/bid-agent/' + _.get(_.first(this.props.auction.round.agents), 'id'), {
      state: this,
      form: this.state.form
    })
    if (res && res.success) {
      // Create new objects to trigger React re-render (don't mutate)
      const updatedRound = {...this.props.auction.round, agents: [res.agent]}
      const updatedAuction = {
        ...this.props.auction,
        round: updatedRound,
        // Use backend calculation - user might still be outbid if agent's max bid isn't high enough
        isOutbid: res.isStillOutbid || false
      }
      
      const updatedAuctions = this.props.auctions.map(a => 
        a.Id === updatedAuction.Id ? updatedAuction : a
      )
      store.dispatch(setAuctions(updatedAuctions))
      this.props.hideModal()
    }
  }

  deactivate = async () => {
    this.setState({
      deactivateBusy: true
    })

    await Http.delete('/bid-agent/' + _.get(_.first(this.props.auction.round.agents), 'id'), {
      state: this,
      form: this.state.form
    })
    this.props.auction.round.agents = []
    store.dispatch(setAuctions(this.props.auctions))
    this.setState({
      deactivateBusy: false
    })
    this.props.hideModal()
  }

  render() {
    const {auction} = this.props
    const header = (
      <div className="px-3 py-2 text-gray-700 font-semibold">
        <span><Trans>Biet-Agent</Trans></span>
      </div>
    )
    const footer = (
      <div className="flex flex-row justify-end p-2">
        <button disabled={this.state.form.busy} onClick={this.props.hideModal} className={"border-0 rounded text-gray-500 hover:bg-gray-200 px-2 py-1 mr-4 " + (this.state.form.busy ? 'cursor-not-allowed opacity-75' : '')} type="button">
          <Trans>Abbrechen</Trans>
        </button>
        {
          auction.round.agents.length ?
            <button disabled={this.state.form.busy} onClick={this.deactivate} className={"border-0 rounded text-gray-500 hover:bg-yellow-200 px-2 py-1 mr-4 text-yellow-700 " + (this.state.form.busy ? 'cursor-not-allowed opacity-75' : '')} type="button">
              <div>
                {
                  this.state.deactivateBusy ?
                    <Icon icon="spinner" className="fa-spin"/>
                    :
                    <Icon icon="ban"/>
                }

                <span> <Trans>Deaktivieren</Trans></span>
              </div>
            </button>
            : null
        }

        <button disabled={this.state.form.busy || this.state.max_bid <= _.toInteger(_.get(this.props.auction, 'highestBid', 0))} onClick={auction.round.agents.length ? this.updateBidAgent : this.saveBidAgent} className={"border-0 rounded bg-gray-300 hover:bg-gray-400 text-gray-700 px-2 py-1 shadow " + (this.state.form.busy || this.state.max_bid <= _.toInteger(_.get(this.props.auction, 'highestBid', 0)) ? 'cursor-not-allowed opacity-75' : '')} type="button">
          <div>
            {
              this.state.form.busy ?
                <Icon icon="spinner" className="fa-spin"/>
                :
                <Icon icon="gavel"/>
            }

            <span> <Trans>Speichern</Trans></span>
          </div>
        </button>
      </div>
    )
    const body = (
      <div className="text-gray-700 px-4 pb-4 flex flex-col">
        <Countdown small={true} auction={auction} timeTillDate={auction.round.EndsOn} />
        <div className="font-semibold leading-relaxed text-xs truncate mb-2">
          {make(this.state.subject.Make)} {model(this.state.subject.Make, this.state.subject.Model)}
        </div>

        <span className="font-semibold text-gray-700">{this.props.auction.highestBid ? <Trans>Höchstgebot</Trans> : null}</span>
        <span className="font-semibold text-yellow-700">{this.props.auction.highestBid ? currency(this.props.auction.highestBid) : <Trans>Keine Gebote</Trans>}</span>

        <span className="font-semibold text-gray-700"><Trans>Ihr Maximalpreis</Trans></span>
        <span className="text-xs text-yellow-700"><Trans>Sollten Sie überboten werden, wird der Bietagent, bis zu diesem Maximalpreis, das Gebot automatisch für Sie überbieten.</Trans></span>
        <input onChange={this.changeMaxBid} name="max_bid" autoFocus={true} value={this.state.max_bid} type="text" placeholder={translate('Maximalgebot')} className="shadow appearance-none block w-full bg-gray-200 text-gray-800 border border-gray-200 rounded py-2 px-3 leading-tight hover:bg-gray-100 focus:bg-gray-100 focus:border-gray-300"/>
      </div>
    )
    return (
      <Modal header={header} footer={footer} body={body}/>
    )
  }
}

export default connect((state: GlobalState) => ({
  auctions: state.auctions.auctions
}))(BidAgentModal)
